/**
 * Tag Editor UI State Service
 * Manages UI state (selection, formatting)
 */

import { Injectable } from '@angular/core'
import { FormattingService } from './formatting.service'
import { EditorInteractionService } from './editor-interaction.service'
import { FormattingState } from '../tag-editor.model'

/**
 * Selection state information
 */
export interface SelectionState {
  readonly hasSelection: boolean
  readonly selectedText: string
  readonly isCollapsed: boolean
}

/**
 * Callbacks for UI state updates
 */
export interface UiStateCallbacks {
  readonly onMarkForCheck: () => void
}

@Injectable({ providedIn: 'root' })
export class TagEditorUiStateService {
  constructor(
    private readonly formattingService: FormattingService,
    private readonly editorInteractionService: EditorInteractionService,
  ) {}

  /**
   * Gets basic selection state information
   */
  private getBasicSelectionState(): {
    readonly selection: Selection | null
    readonly hasRange: boolean
    readonly isCollapsed: boolean
    readonly selectedText: string
    readonly hasSelection: boolean
  } {
    const selection: Selection | null = window.getSelection()
    const hasRange: boolean = !!(selection && selection.rangeCount > 0)
    const isCollapsed: boolean = hasRange ? selection!.getRangeAt(0).collapsed : true
    const selectedText: string = this.editorInteractionService.getSelectedText()
    const hasSelection: boolean = !!(selectedText && selectedText.trim().length > 0)

    return {
      selection,
      hasRange,
      isCollapsed,
      selectedText,
      hasSelection,
    }
  }

  /**
   * Updates text selection state
   */
  public updateTextSelectionState(
    editor: HTMLElement,
    currentState: { value: boolean },
    callbacks: UiStateCallbacks,
  ): boolean {
    const { selection, hasRange, isCollapsed, hasSelection } = this.getBasicSelectionState()

    const hasNonCollapsedRange: boolean = hasRange && !isCollapsed

    const selectionInEditor: boolean = !!(
      hasRange &&
      editor &&
      selection &&
      selection.anchorNode &&
      editor.contains(selection.anchorNode)
    )

    const finalHasSelection: boolean = (hasSelection || hasNonCollapsedRange) && selectionInEditor

    if (currentState.value !== finalHasSelection) {
      currentState.value = finalHasSelection
      callbacks.onMarkForCheck()
      return true
    }

    return false
  }

  /**
   * Gets formatting state
   */
  public getFormattingState(): FormattingState {
    return this.formattingService.getFormattingState()
  }

  /**
   * Gets selection state
   */
  public getSelectionState(_editor: HTMLElement): SelectionState {
    const { hasSelection, selectedText, isCollapsed } = this.getBasicSelectionState()

    return {
      hasSelection,
      selectedText: selectedText || '',
      isCollapsed,
    }
  }
}
